const PrivacyPolicy = () => {
  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto">
        <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Legal</p>
        <h1 className="text-4xl md:text-5xl text-[#EDEDED] mb-4" data-testid="privacy-title">Privacy Policy</h1>
        <p className="text-[#A1A1AA] mb-12">Last updated: March 30, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-[#EDEDED] leading-relaxed">
            Breedling&trade; (&ldquo;Breedling,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates an online marketplace platform located at{" "}
            <a href="https://breedling.com" className="text-[#D4AF37] hover:underline">https://breedling.com</a>{" "}
            (the &ldquo;Platform&rdquo;). This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you use our services, including our marketplace, live auction engine, transporter registry, 
            vendor directory, and reputation system.
          </p>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">1. Information We Collect</h2>
            <p className="text-[#A1A1AA] mb-4">We may collect the following types of information:</p>
            
            <h3 className="text-lg text-[#D4AF37] mb-2">Personal Information</h3>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 mb-4">
              <li>Name, email address, and profile picture (via Google OAuth)</li>
              <li>Phone number and business contact details (for service providers and transporters)</li>
              <li>Billing and payment information (processed and stored by Stripe; Breedling does not store full card numbers)</li>
              <li>Breeder, transporter, or vendor profile details (business name, service area, licensing information)</li>
              <li>Identity verification information for sellers and service providers</li>
            </ul>

            <h3 className="text-lg text-[#D4AF37] mb-2">Usage Information</h3>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 mb-4">
              <li>IP address, device data, browser type, and operating system</li>
              <li>Pages viewed, actions taken, and features used</li>
              <li>Log files, cookies, and session data</li>
            </ul>

            <h3 className="text-lg text-[#D4AF37] mb-2">Transaction &amp; Activity Information</h3>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 mb-4">
              <li>Subscription purchases and tier selections</li>
              <li>Marketplace transactions between buyers and sellers</li>
              <li>Auction bids, watch lists, and auction outcomes</li>
              <li>Reputation scores, tier status, and payment history</li>
              <li>Notification preferences and delivery history</li>
            </ul>

            <h3 className="text-lg text-[#D4AF37] mb-2">Payment Method Information</h3>
            <p className="text-[#A1A1AA] mb-4">
              To participate in auctions, bidders must register a payment method (credit or debit card) through Stripe. 
              Breedling stores a reference to the Stripe payment method (e.g., card brand and last four digits) for 
              display purposes. Full card details are held exclusively by Stripe in accordance with PCI DSS standards.
            </p>

            <p className="text-[#A1A1AA] italic">
              Breedling does not store full payment card numbers. All payment processing is handled by Stripe.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">2. How We Use Your Information</h2>
            <p className="text-[#A1A1AA] mb-2">We use information to:</p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1">
              <li>Operate and maintain the Platform, including marketplace, auctions, and registries</li>
              <li>Process subscriptions, marketplace payments, and auction transactions</li>
              <li>Verify sellers, transporters, and service providers</li>
              <li>Calculate and maintain reputation scores and bid eligibility</li>
              <li>Send transactional notifications (outbid alerts, auction results, payment reminders, verification updates)</li>
              <li>Provide customer support and resolve disputes</li>
              <li>Prevent fraud, shill bidding, and abuse of the Platform</li>
              <li>Improve Platform functionality and user experience</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">3. Sharing of Information</h2>
            <p className="text-[#A1A1AA] mb-2">We may share information with:</p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 mb-4">
              <li><strong className="text-[#EDEDED]">Stripe</strong> for payment processing, subscription management, bidder verification, and auction settlement</li>
              <li><strong className="text-[#EDEDED]">Resend</strong> for transactional email delivery (outbid alerts, auction notifications, verification updates)</li>
              <li><strong className="text-[#EDEDED]">Google</strong> for authentication (OAuth)</li>
              <li>Identity-verification providers as needed for seller and transporter onboarding</li>
              <li>Hosting and analytics providers for Platform operation and improvement</li>
              <li>Law enforcement or regulators if required by law or to protect our rights</li>
            </ul>
            <p className="text-[#A1A1AA] mb-4">
              When you participate in an auction, your reputation tier (Trusted, Standard, or Risky) may be visible 
              to sellers and other bidders to promote transparency and trust.
            </p>
            <p className="text-[#D4AF37] font-medium">We do not sell your personal information to third parties.</p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">4. Auction &amp; Reputation Data</h2>
            <p className="text-[#A1A1AA] mb-4">
              Your bid history, auction outcomes, and payment behavior are used to calculate your Buyer Reputation Score. 
              This score is maintained internally and may affect your ability to participate in certain auctions:
            </p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 mb-4">
              <li>Your reputation tier is visible to sellers when you bid on their auctions</li>
              <li>Users with low reputation scores may be restricted from high-value auctions</li>
              <li>Reputation data is not shared with external parties</li>
            </ul>
            <p className="text-[#A1A1AA]">
              You may view your reputation score and history in your Dashboard at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">5. Marketplace Disclaimer</h2>
            <p className="text-[#A1A1AA]">
              Breedling is a technology platform only. We do not breed, own, transport, inspect, or sell animals. 
              We do not employ transporters or service providers listed in our registries. 
              All transactions, services, and arrangements occur between independent users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">6. Cookies and Tracking</h2>
            <p className="text-[#A1A1AA]">
              We use cookies and similar technologies to operate the Platform, maintain authentication sessions, 
              and improve your experience. You may disable cookies through your browser settings, 
              though this may affect certain Platform functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">7. Data Security</h2>
            <p className="text-[#A1A1AA] mb-4">
              We implement reasonable technical and organizational safeguards to protect your information, including:
            </p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1 mb-4">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>PCI DSS-compliant payment processing via Stripe</li>
              <li>Secure authentication via Google OAuth</li>
              <li>Access controls and role-based admin permissions</li>
            </ul>
            <p className="text-[#A1A1AA]">
              No system is completely secure. We cannot guarantee absolute security of your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">8. Data Retention</h2>
            <p className="text-[#A1A1AA]">
              We retain your personal information for as long as your account is active or as needed to provide our services. 
              Transaction records, bid history, and reputation data may be retained longer for compliance, dispute resolution, 
              and fraud prevention purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">9. Your Rights</h2>
            <p className="text-[#A1A1AA] mb-4">
              Depending on your location, you may have rights to:
            </p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data (subject to legal retention requirements)</li>
              <li>Object to or restrict certain processing of your data</li>
              <li>Port your data to another service</li>
            </ul>
            <p className="text-[#A1A1AA] mt-4">
              To exercise these rights, contact us at{" "}
              <a href="mailto:support@breedling.com" className="text-[#D4AF37] hover:underline">support@breedling.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">10. Children</h2>
            <p className="text-[#A1A1AA]">
              Breedling is not intended for individuals under 18. We do not knowingly collect personal information 
              from children under 18. If you believe a child has provided us with personal information, 
              please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">11. Third-Party Services</h2>
            <p className="text-[#A1A1AA] mb-4">
              Our Platform integrates with third-party services that have their own privacy policies:
            </p>
            <ul className="list-disc list-inside text-[#A1A1AA] space-y-1">
              <li><strong className="text-[#EDEDED]">Stripe</strong> &mdash; <a href="https://stripe.com/privacy" className="text-[#D4AF37] hover:underline" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
              <li><strong className="text-[#EDEDED]">Google</strong> &mdash; <a href="https://policies.google.com/privacy" className="text-[#D4AF37] hover:underline" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">12. Changes</h2>
            <p className="text-[#A1A1AA]">
              We may update this Privacy Policy at any time. Material changes will be posted on this page 
              with an updated "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl text-[#EDEDED] mb-4">13. Contact</h2>
            <p className="text-[#A1A1AA]">
              Email: <a href="mailto:support@breedling.com" className="text-[#D4AF37] hover:underline">support@breedling.com</a>
            </p>
            <p className="text-[#A1A1AA]">
              Website: <a href="https://breedling.com/support" className="text-[#D4AF37] hover:underline">https://breedling.com/support</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
